/**
 * VerifyTrainingButton — Batch 18-E12
 *
 * Smallest manager-facing UI for the 18-E11-hardened manager
 * verification backend. Renders a "Verify Training" button + small
 * confirmation dialog with an optional note. ALL authority lives in
 * the verify-training-completion Edge Function + verify_training_completion
 * SECURITY DEFINER RPC; this component performs no direct DB writes.
 *
 * Client-side gating (display-only — the Edge Function remains the
 * authoritative reject point):
 *   - Hidden when the `managerTrainingVerification` feature flag is off.
 *   - Hidden when the session is already manager-verified.
 *   - Hidden when the session has no manual completion timestamp.
 *   - Hidden when completion_status is not 'completed' or 'partial'.
 *
 * This component does NOT:
 *   - write to training_consumption_sessions directly
 *   - write to training_completion_verifications directly
 *   - emit training_effectiveness_events
 *   - reference 'manager_verified' as an event type
 *   - know about session_token / extension_evidence / Companion code
 *   - implement revocation (deferred to a later batch)
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useVerifyTrainingCompletion } from '@/hooks/useVerifyTrainingCompletion';

interface VerifyTrainingButtonProps {
  sessionId: string;
  completionStatus: string | null;
  manualMarkedCompleteAt: string | null;
  managerVerifiedAt: string | null;
  disabled?: boolean;
  artifactTitle?: string;
  technicianName?: string;
  onVerified?: () => void;
}

// Friendly mapping for the Edge Function's generic error strings.
// The Edge Function returns the message body { error: '...' } so we
// match on the message content; falls back to a generic message.
function describeError(message: string | undefined): string {
  const msg = (message ?? '').toLowerCase();
  if (msg.includes('already verified')) {
    return 'This training is already verified.';
  }
  if (msg.includes('self-verification')) {
    return 'You cannot verify your own training session.';
  }
  if (msg.includes('not authorized')) {
    return 'You do not have permission to verify training for this technician.';
  }
  if (msg.includes('verifiable completion state')) {
    return 'This session cannot be verified — only completed or partial sessions are eligible.';
  }
  if (msg.includes('manually marked complete')) {
    return 'This session must be marked complete by the technician before it can be verified.';
  }
  if (msg.includes('session not found')) {
    return 'Training session no longer exists.';
  }
  return 'Verification failed. Please try again.';
}

export function VerifyTrainingButton({
  sessionId,
  completionStatus,
  manualMarkedCompleteAt,
  managerVerifiedAt,
  disabled = false,
  artifactTitle,
  technicianName,
  onVerified,
}: VerifyTrainingButtonProps) {
  const flagEnabled = useFeatureFlag('managerTrainingVerification');
  const verify = useVerifyTrainingCompletion();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');

  // Display-only gating. The Edge Function is the authoritative reject.
  if (!flagEnabled) return null;
  if (managerVerifiedAt) return null;
  if (!manualMarkedCompleteAt) return null;
  if (completionStatus !== 'completed' && completionStatus !== 'partial') {
    return null;
  }

  const handleVerify = async () => {
    try {
      await verify.mutateAsync({
        sessionId,
        note: note.trim() ? note.trim() : undefined,
      });
      toast.success(
        artifactTitle
          ? `Confirmed: ${artifactTitle}`
          : 'Training confirmed',
      );
      setNote('');
      setOpen(false);
      onVerified?.();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message ?? '')
          : '';
      toast.error(describeError(message));
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="gap-1"
        disabled={disabled || verify.isPending}
        onClick={() => setOpen(true)}
        data-testid="verify-training-button"
      >
        <ShieldCheck className="h-3 w-3" />
        Verify Training
      </Button>

      <Dialog open={open} onOpenChange={(next) => !verify.isPending && setOpen(next)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Verify Training Completion
            </DialogTitle>
            <DialogDescription>
              {technicianName && artifactTitle
                ? `Confirm that ${technicianName} completed "${artifactTitle}".`
                : artifactTitle
                ? `Confirm completion of "${artifactTitle}".`
                : 'Confirm this training was completed.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label
              htmlFor="verify-training-note"
              className="text-xs font-medium text-muted-foreground"
            >
              Note (optional)
            </label>
            <Textarea
              id="verify-training-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional verification context (visible to managers and the technician)."
              rows={3}
              disabled={verify.isPending}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={verify.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleVerify} disabled={verify.isPending}>
              {verify.isPending ? 'Verifying…' : 'Verify'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
